/**
 * Layer-only preview renderer.
 * Uses layers from control-preview API. No fallback to page-based components.
 * Missing or empty layers → explicit "Preview unavailable" error state.
 */

import { Box, Paper, Typography } from '@mui/material';
import { enrichMediaPreviewLayersFromPayload } from '@/helpers/preview/enrich-media-preview-layers.helper';
import { isPreviewLayersArray } from '@/helpers/preview/preview-layer-utils';
import { PreviewStaticLayerRenderer } from './preview-static-layer-renderer';
import type { PresentationSlideNavigationScope } from '@/components/preview/preview-presentation-slides-layer.types';
import type { PlayerEventSettings } from '@/types/preview.types';

interface PreviewRendererProps {
  /** Player sync data from control-preview API (payload, settings, layers) */
  playerSyncData: Record<string, unknown>;
  /** Tight 16:9 layout for presentation slide previews (media modal). */
  presentationPreview?: boolean;
  /** Local auto-loop play/pause overlay; false in single-media PreviewModal (cue play is separate). */
  sequencePlaybackControls?: boolean;
  /** Override baked inline HTML colors with settings.textColor (Customize live preview). */
  forceHtmlTextColor?: boolean;
  /** Perform item preview uses live; media library keeps local. */
  slideNavigationScope?: PresentationSlideNavigationScope;
}

function resolvePreviewUnavailableMessage(playerSyncData: Record<string, unknown>): string {
  const media = (
    playerSyncData.payload as { media?: { playoutReady?: boolean; processingStatus?: string } } | undefined
  )?.media;
  if (media?.processingStatus === 'rejected') {
    return 'This file could not be processed for preview. Check processing details on the media row.';
  }
  if (media && media.playoutReady === false) {
    return 'Preview is still being prepared. Try again once processing finishes.';
  }
  if (playerSyncData.page === 'media') {
    return 'Preview could not be loaded. Try again or contact an event manager if this keeps happening.';
  }
  return 'Preview could not be loaded for this content.';
}

function PreviewUnavailable({ message }: { message: string }) {
  return (
    <Paper
      elevation={2}
      sx={{
        height: '100%',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        p: 4,
        bgcolor: '#f5f5f5',
      }}
    >
      <Typography variant="h5" color="error" gutterBottom>
        Preview unavailable
      </Typography>
      <Typography variant="body1" color="text.secondary" align="center">
        {message}
      </Typography>
    </Paper>
  );
}

export function PreviewRenderer({
  playerSyncData,
  presentationPreview = false,
  sequencePlaybackControls,
  forceHtmlTextColor = false,
  slideNavigationScope,
}: PreviewRendererProps) {
  if (!playerSyncData || typeof playerSyncData !== 'object') {
    return <PreviewUnavailable message="Preview could not be loaded." />;
  }

  const resolvedSyncData = enrichMediaPreviewLayersFromPayload(playerSyncData);
  const layers = resolvedSyncData.layers as unknown;
  const settings = (resolvedSyncData.settings ?? resolvedSyncData.theme) as PlayerEventSettings | undefined;

  if (!isPreviewLayersArray(layers) || layers.length === 0) {
    return <PreviewUnavailable message={resolvePreviewUnavailableMessage(resolvedSyncData)} />;
  }

  const resolvedScope = slideNavigationScope ?? (presentationPreview ? ('local' as const) : undefined);

  const renderer = (
    <PreviewStaticLayerRenderer
      layers={layers}
      settings={settings}
      height="100%"
      presentationPreview={presentationPreview}
      slideNavigationScope={resolvedScope}
      sequencePlaybackControls={sequencePlaybackControls}
      forceHtmlTextColor={forceHtmlTextColor}
    />
  );

  // Always fill the modal's 16:9 shell. Non-presentation used to rely on flex:1 inside a
  // tall dialog; with the shared aspect-ratio shell that collapses to 0 without height:100%.
  if (presentationPreview) {
    return <Box sx={{ width: '100%', height: '100%', overflow: 'hidden' }}>{renderer}</Box>;
  }

  return (
    <Paper
      elevation={2}
      sx={{
        width: '100%',
        height: '100%',
        overflow: 'hidden',
      }}
    >
      {renderer}
    </Paper>
  );
}
